import { useTheme } from '../../providers/GigerProvider/useTheme.js';
import { createPopper } from '@popperjs/core';
import { useState, useMemo, useLayoutEffect } from 'react';
import useMeasure from '../useMeasure/useMeasure.js';

const DEFAULT_ARROW_SIZE = 8;
const DEFAULT_PADDING = 12;
const getPopoutWithArrowStyles = function (theme) {
  let arrowSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_ARROW_SIZE;
  let placement = arguments.length > 2 ? arguments[2] : undefined;
  let backgroundColor = arguments.length > 3 ? arguments[3] : undefined;
  let borderSize = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 1;
  let borderColor = arguments.length > 5 ? arguments[5] : undefined;
  let offset = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : 0;
  const getArrowCommonStyles = (theme, backgroundColor) => {
    return {
      borderBottom: `${arrowSize}px solid ${backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : theme.palette.white}`,
      borderLeft: `${arrowSize}px solid transparent`,
      borderRight: `${arrowSize}px solid transparent`,
      height: arrowSize,
      position: 'absolute',
      width: arrowSize * 2
    };
  };
  const offsetAlignment = placement.split('-')[1];
  const offsetValue = offsetAlignment === 'end' ? offset * -1 : offset;
  return {
    '.arrow': {
      visibility: 'hidden',
      // Arrow content
      '::after': {
        ...getArrowCommonStyles(theme, backgroundColor || theme.palette.white)
      },
      // Arrow border
      '::before': {
        ...getArrowCommonStyles(theme, borderColor || theme.palette.gray[200])
      },
      '::after, ::before': {
        visibility: 'visible',
        content: '""'
      }
    },
    "&[data-popper-placement^='top'] > .arrow": {
      bottom: 0,
      '::after, ::before': {
        left: `${offsetValue - arrowSize}px`,
        transform: 'rotate(180deg)'
      },
      '::before': {
        top: borderSize
      }
    },
    "&[data-popper-placement^='bottom'] > .arrow": {
      top: `-${arrowSize + borderSize}px`,
      '::after, ::before': {
        left: `${offsetValue - arrowSize}px`
      },
      '::after': {
        top: borderSize
      }
    },
    "&[data-popper-placement^='left'] > .arrow": {
      right: 0,
      '::after, ::before': {
        transform: 'rotate(90deg)',
        top: `${offsetValue - arrowSize / 2}px`,
        right: `-${arrowSize + arrowSize / 2}px`
      },
      '::before': {
        right: `-${arrowSize + arrowSize / 2 + borderSize}px`
      }
    },
    "&[data-popper-placement^='right'] > .arrow": {
      '::after, ::before': {
        transform: 'rotate(-90deg)',
        top: `${offsetValue - arrowSize / 2}px`,
        left: `-${arrowSize + arrowSize / 2}px`
      },
      '::before': {
        left: `-${arrowSize + arrowSize / 2 + borderSize}px`
      }
    }
  };
};
const usePopout = (reference, placement, configuration) => {
  const theme = useTheme();
  const [state, setState] = useState();
  const [arrowElement, setArrowElement] = useState(null);
  const [popoutElement, setPopoutElement] = useState(null);
  const [ref, {
    width
  }] = useMeasure();
  const {
    offset,
    enabled,
    preventOverflow = true,
    arrow: arrowConfiguration,
    overflowPadding
  } = configuration || {};
  const renderPopout = useMemo(() => enabled && reference != null && popoutElement != null && width !== 0, [enabled, popoutElement, reference, width]);
  useLayoutEffect(() => {
    reference && ref(reference);
  }, [ref, reference]);
  useLayoutEffect(() => {
    let popperInstance = null;
    if (renderPopout && reference && popoutElement) {
      popperInstance = createPopper(reference, popoutElement, {
        placement,
        strategy: 'fixed',
        onFirstUpdate: state => {
          setState(state);
          if (arrowElement) {
            arrowElement.setAttribute('class', 'arrow');
            arrowElement.setAttribute('aria-hidden', 'true');
          }
        },
        modifiers: [{
          name: 'arrow',
          enabled: !!arrowElement,
          options: {
            element: arrowElement,
            padding: offset ? 0 : DEFAULT_PADDING
          }
        }, {
          name: 'preventOverflow',
          enabled: preventOverflow,
          options: {
            padding: overflowPadding || 0
          }
        }, {
          name: 'hide',
          options: {
            isReferenceHidden: true
          }
        }, {
          name: 'offset',
          options: {
            offset: [0, arrowConfiguration !== null && arrowConfiguration !== void 0 && arrowConfiguration.arrowSize ? (arrowConfiguration === null || arrowConfiguration === void 0 ? void 0 : arrowConfiguration.arrowSize) + (arrowConfiguration === null || arrowConfiguration === void 0 ? void 0 : arrowConfiguration.arrowSize) / 2 : arrowElement ? DEFAULT_PADDING : DEFAULT_ARROW_SIZE]
          }
        }]
      });
    }
    return () => {
      var _popperInstance;
      (_popperInstance = popperInstance) === null || _popperInstance === void 0 ? void 0 : _popperInstance.destroy();
    };
  }, [arrowConfiguration === null || arrowConfiguration === void 0 ? void 0 : arrowConfiguration.arrowSize, arrowElement, offset, overflowPadding, placement, popoutElement, preventOverflow, ref, reference, renderPopout]);
  return {
    setPopoutElement,
    setArrowElement,
    popoutWithArrowStyles: getPopoutWithArrowStyles(theme, arrowConfiguration === null || arrowConfiguration === void 0 ? void 0 : arrowConfiguration.arrowSize, (state === null || state === void 0 ? void 0 : state.placement) || placement, arrowConfiguration === null || arrowConfiguration === void 0 ? void 0 : arrowConfiguration.backgroundColor, arrowConfiguration === null || arrowConfiguration === void 0 ? void 0 : arrowConfiguration.borderSize, arrowConfiguration === null || arrowConfiguration === void 0 ? void 0 : arrowConfiguration.borderColor, offset),
    state
  };
};

export { usePopout };
//# sourceMappingURL=usePopout.js.map
